import { AdvFilterCommand, AdvFilterPicker, AdvFilterResult, AutoCompleteKeyWord } from "components/AdvFilter";
import { CmdGroup, CmdTopicNoSubs, CmdUser, CommandLabels } from "components/AdvFilter/CommonCommands";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPaper from "components/MuiExtras/MuiPaper";
import { WithSnackbar, withSnackbar } from "components/SnackbarProvider/Snackbar";
import { MemberType } from "models/ApiEnums";
import { DashboardKnowledgeIndicatorsDetailColumn, DashboardKnowledgeIndicatorsGraphGroup, DashboardKnowledgeIndicatorsSummaryColumn, DashboardUserAndGroupsAndManagersBaseFilter } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { KnowledgeListOptionsType } from "views/Dashboards/components/KnowledgeCommons";
import KnowledgeTestFilter from "views/Dashboards/KnowledgeTestDashboard/KnowledgeTestFilter";
import { KnowledgeTestIndicatorsGraph } from "views/Dashboards/KnowledgeTestDashboard/KnowledgeTestIndicatorsGraph";
import KnowledgeTestIndicatorsTiles from "views/Dashboards/KnowledgeTestDashboard/KnowledgeTestIndicatorsTiles";
import { KnowledgeTestTabs, TabOptions } from "views/Dashboards/KnowledgeTestDashboard/KnowledgeTestTabs";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    indicatorTile: DashboardKnowledgeIndicatorsSummaryColumn;
    advFilters: AdvFilterResult[];
}

interface IProps {
    onTabChange: (tabOptions: TabOptions) => void;
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class KnowledgeTestIndicatorsDashboard extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            indicatorTile: DashboardKnowledgeIndicatorsSummaryColumn.Knowledge,
            advFilters: []
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Dashboards.Components.Commons.KnowledgeTestIndicators." + r);
    }

    ///
    private handleSelectTopic = (topicId: string, name: string, thumbnailUrl: string) => {
        this.setState(({ advFilters }) => {

            advFilters = advFilters.filter(p => p.keyword != AutoCompleteKeyWord);

            advFilters.push({
                keyword: AutoCompleteKeyWord,
                value: { key: topicId, label: name, thumbnailUrl: thumbnailUrl } as AutoCompleteDataModel,
                text: name
            });

            return { advFilters };
        });

        this.props.snackbar.showMessage(this.props.t("Views.Dashboards.KnowledgeTest.Filter.FilteringTopicText", { name: name }));
    };

    ///
    private handleSelectUser = (userId: string, name: string) => {

        this.setState(({ advFilters }) => {

            advFilters = advFilters.filter(p => p.keyword != CmdUser);

            advFilters.push({
                keyword: CmdUser,
                value: userId,
                text: name
            });

            return { advFilters };
        });

        this.props.snackbar.showMessage(this.props.t("Views.Dashboards.KnowledgeTest.Filter.FilteringTestText", { name: name }));
    };

    ///
    private getFilter(): DashboardUserAndGroupsAndManagersBaseFilter {
        const { advFilters } = this.state;

        var topicFilter = advFilters.find(p => p.keyword == AutoCompleteKeyWord);

        const user = advFilters.find(p => p.keyword == CmdUser);
        const group = advFilters.find(p => p.keyword == CmdGroup);
        const includeSubtopics = advFilters.find(p => p.keyword == CmdTopicNoSubs) == null;

        return {
            topicId: topicFilter == null
                ? null
                : topicFilter.value.key,
            includeSubtopics: includeSubtopics,

            memberId: user == null && group == null
                ? null
                : user == null
                    ? group.value
                    : user.value,

            managerId: null,
            managerDeepTeam: null,

            memberType: user == null && group == null
                ? null
                : user == null
                    ? MemberType.Group
                    : MemberType.User,
        };
    }

    ///
    private get dashboardKnowledgeTestCommands(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: CmdTopicNoSubs, displayName: cmdLabels[CmdTopicNoSubs], picker: null, group: 1, canRepeat: false },
            { keyword: CmdUser, displayName: cmdLabels[CmdUser], picker: AdvFilterPicker.User, group: 2, canRepeat: false, optionGroup: "member" },
            { keyword: CmdGroup, displayName: cmdLabels[CmdGroup], picker: AdvFilterPicker.Group, group: 2, canRepeat: false, optionGroup: "member" },
        ];
    }

    ///
    private get dashboardKnowledgeTestIndicatorsListOptions(): KnowledgeListOptionsType[] {
        return [
            {
                column: DashboardKnowledgeIndicatorsSummaryColumn.Knowledge,
                options: [
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicBetter, text: this.res("KnowledgeTopicBetter"), group: "anyUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicWorst, text: this.res("KnowledgeTopicWorst"), group: "anyUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicLevel, text: this.res("KnowledgeTopicLevel"), group: "anyUser", useLevel: true, graph: "topic" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicQt, text: this.res("KnowledgeTopicQt"), group: "anyUser", useLevel: true, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserBetter, text: this.res("KnowledgeUserBetter"), group: "anyTopic", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserWorst, text: this.res("KnowledgeUserWorst"), group: "anyTopic", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserLevel, text: this.res("KnowledgeUserLevel"), group: "anyTopic", useLevel: true, graph: "user" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserQt, text: this.res("KnowledgeUserQt"), group: "anyTopic", useLevel: true, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicBetter, text: this.res("KnowledgeUserTopicBetter"), group: "singleUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicWorst, text: this.res("KnowledgeUserTopicWorst"), group: "singleUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel, text: this.res("KnowledgeUserTopicBetterLevel"), group: "singleUser", useLevel: true, graph: "all" }
                ],
            },
            {
                column: DashboardKnowledgeIndicatorsSummaryColumn.Result,
                options: [
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicBetter, text: this.res("ResultTopicBetter"), group: "anyUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicWorst, text: this.res("ResultTopicWorst"), group: "anyUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicLevel, text: this.res("ResultTopicLevel"), group: "anyUser", useLevel: false, graph: "topic" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserBetter, text: this.res("ResultUserBetter"), group: "anyTopic", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserWorst, text: this.res("ResultUserWorst"), group: "anyTopic", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserLevel, text: this.res("ResultUserLevel"), group: "anyTopic", useLevel: true, graph: "user" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicBetter, text: this.res("ResultUserTopicBetter"), group: "singleUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicWorst, text: this.res("ResultUserTopicWorst"), group: "singleUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel, text: this.res("ResultUserTopicBetterLevel"), group: "singleUser", useLevel: true, graph: "all" }
                ]
            },
            {
                column: DashboardKnowledgeIndicatorsSummaryColumn.Trust,
                options: [
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicBetter, text: this.res("TrustTopicBetter"), group: "anyUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicWorst, text: this.res("TrustTopicWorst"), group: "anyUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicLevel, text: this.res("TrustTopicLevel"), group: "anyUser", useLevel: true, graph: "topic" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserBetter, text: this.res("TrustUserBetter"), group: "anyTopic", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserWorst, text: this.res("TrustUserWorst"), group: "anyTopic", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserLevel, text: this.res("TrustUserLevel"), group: "anyTopic", useLevel: true, graph: "user" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicBetter, text: this.res("TrustUserTopicBetter"), group: "singleUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicWorst, text: this.res("TrustUserTopicWorst"), group: "singleUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel, text: this.res("TrustUserTopicBetterLevel"), group: "singleUser", useLevel: true, graph: "all" }
                ]
            },
            {
                column: DashboardKnowledgeIndicatorsSummaryColumn.Completed,
                options: [
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicBetter, text: this.res("CompletedTopicBetter"), group: "anyUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicWorst, text: this.res("CompletedTopicWorst"), group: "anyUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicLevel, text: this.res("CompletedTopicLevel"), group: "anyUser", useLevel: true, graph: "topic" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserBetter, text: this.res("CompletedUserBetter"), group: "anyTopic", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserWorst, text: this.res("CompletedUserWorst"), group: "anyTopic", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserLevel, text: this.res("CompletedUserLevel"), group: "anyTopic", useLevel: true, graph: "user" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicBetter, text: this.res("CompletedUserTopicBetter"), group: "singleUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicWorst, text: this.res("CompletedUserTopicWorst"), group: "singleUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel, text: this.res("CompletedUserTopicBetterLevel"), group: "singleUser", useLevel: true, graph: "all" }
                ]
            },
            {
                column: DashboardKnowledgeIndicatorsSummaryColumn.Revision,
                options: [
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicBetter, text: this.res("RevisionTopicBetter"), group: "anyUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicWorst, text: this.res("RevisionTopicWorst"), group: "anyUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.TopicLevel, text: this.res("RevisionTopicLevel"), group: "anyUser", useLevel: true, graph: "topic" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserBetter, text: this.res("RevisionUserBetter"), group: "anyTopic", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserWorst, text: this.res("RevisionUserWorst"), group: "anyTopic", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserLevel, text: this.res("RevisionUserLevel"), group: "anyTopic", useLevel: true, graph: "user" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicBetter, text: this.res("RevisionUserTopicBetter"), group: "singleUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicWorst, text: this.res("RevisionUserTopicWorst"), group: "singleUser", useLevel: false, graph: "all" },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel, text: this.res("RevisionUserTopicBetterLevel"), group: "singleUser", useLevel: true, graph: "all" }
                ]
            },
            {
                column: DashboardKnowledgeIndicatorsSummaryColumn.Engagement,
                options: [
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserParticipate, text: this.res("EngagementUserParticipate"), group: "", useLevel: false },
                    { key: DashboardKnowledgeIndicatorsDetailColumn.UserParticipateLevel, text: this.res("EngagementUserParticipateLevel"), group: "", useLevel: true }
                ]
            }];
    }

    ///
    public render() {
        const { advFilters, indicatorTile } = this.state;

        const filter = this.getFilter();

        return (
            <>
                <KnowledgeTestFilter
                    advFilters={advFilters}
                    commands={this.dashboardKnowledgeTestCommands}
                    onFilterChange={(advFilters) => this.setState({ advFilters })}
                />

                <KnowledgeTestTabs
                    onTabChange={this.props.onTabChange}
                    currentTab={TabOptions.Indicator}
                />

                <KnowledgeTestIndicatorsTiles
                    defaultGraphGroup={DashboardKnowledgeIndicatorsGraphGroup.Topic}
                    activeTile={indicatorTile}
                    filter={filter}
                    onChange={
                        (tile) => {
                            this.setState({ indicatorTile: tile });
                        }
                    }
                />

                <MuiContainer margin="normal">
                    <MuiPaper borderVariant="accentedRound" borderColor="success">
                        <KnowledgeTestIndicatorsGraph
                            {...this.props}
                            defaultGraphGroup={DashboardKnowledgeIndicatorsGraphGroup.Topic}
                            tile={indicatorTile}
                            filter={filter}
                            listOptions={this.dashboardKnowledgeTestIndicatorsListOptions}
                            onSelectUser={this.handleSelectUser}
                            onSelectTopic={this.handleSelectTopic} />
                    </MuiPaper>
                </MuiContainer>
            </>
        );
    }
}

export default withSnackbar(withTranslation()(KnowledgeTestIndicatorsDashboard));